import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import { registerLocaleData } from '@angular/common';
import { provideHttpClient, withInterceptors, withXhr } from '@angular/common/http';
import localeDe from '@angular/common/locales/de';
import localeEn from '@angular/common/locales/en-GB';
import localeMk from '@angular/common/locales/mk';
import { APP_INITIALIZER, ApplicationConfig, DEFAULT_CURRENCY_CODE, ErrorHandler, LOCALE_ID } from '@angular/core';
import { MAT_RIPPLE_GLOBAL_OPTIONS, RippleGlobalOptions } from '@angular/material/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { Router, provideRouter } from '@angular/router';
import { TranslocoService, provideTransloco } from '@jsverse/transloco';
import { provideTranslocoMessageformat } from '@jsverse/transloco-messageformat';
import { withNgxsReduxDevtoolsPlugin } from '@ngxs/devtools-plugin';
import { withNgxsStoragePlugin } from '@ngxs/storage-plugin';
import { provideStore } from '@ngxs/store';
import * as Sentry from '@sentry/angular';
import { provideQuillConfig } from 'ngx-quill/config';
import { provideToastr } from 'ngx-toastr';
import { withNgxsResetPlugin } from 'ngxs-reset-plugin';
import { environment } from 'src/environments/environment';
import { routes } from './app.routes';
import { LANGUAGE_KEY } from './core/constants/languages.const';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { loadingInterceptor } from './core/interceptors/loading.interceptor';
import { urlInterceptor } from './core/interceptors/url.interceptor';
import { provideMaterialDateConfig } from './core/providers/material-date.providers';
import { TranslocoHttpLoader } from './core/services/transloco-loader.service';
import { persistList, stateList } from './core/store/state-list';

// Register locale data for all supported languages
registerLocaleData(localeEn, 'en');
registerLocaleData(localeMk, 'mk');
registerLocaleData(localeDe, 'de');

// Global configuration for Material Ripple
// This disables the ripple effect globally
const C_GlobalRippleConfig: RippleGlobalOptions = {
  disabled: true,
  animation: {
    enterDuration: 0,
    exitDuration: 0,
  },
};

export const appConfig: ApplicationConfig = {
  providers: [
    // Router providers
    provideRouter(routes),
    // Http client providers
    provideHttpClient(withXhr(), withInterceptors([urlInterceptor, authInterceptor, loadingInterceptor])),
    // Animation providers
    provideAnimations(),
    // Transloco providers
    provideTransloco({
      config: {
        availableLangs: ['en', 'mk', 'de'],
        defaultLang: 'en',
        fallbackLang: 'en',
        prodMode: environment.production,
      },
      loader: TranslocoHttpLoader,
    }),
    provideTranslocoMessageformat({
      locales: ['en', 'mk', 'de'],
    }),

    {
      provide: APP_INITIALIZER,
      multi: true,
      useFactory: preloadUserLanguage,
      deps: [TranslocoService],
    },
    // Toastr providers
    provideToastr({
      timeOut: 2500,
      preventDuplicates: true,
      closeButton: true,
    }),
    // Add to appConfig.providers:
    {
      provide: OVERLAY_DEFAULT_CONFIG,
      useValue: { usePopover: false },
    },
    {
      provide: MAT_RIPPLE_GLOBAL_OPTIONS,
      useValue: C_GlobalRippleConfig,
    },
    // Locale configuration for date pipe based on user language
    {
      provide: LOCALE_ID,
      useFactory: () => {
        const lang = localStorage.getItem(LANGUAGE_KEY) || 'en';
        return lang; // Returns 'en', 'mk', or 'de'
      },
    },
    // Material date adapter: start weeks on Monday and follow the active language
    provideMaterialDateConfig(),
    { provide: DEFAULT_CURRENCY_CODE, useValue: 'ден' },

    ...(environment.sentry.dsn
      ? [
          {
            provide: ErrorHandler,
            useValue: Sentry.createErrorHandler(),
          },
          {
            provide: Sentry.TraceService,
            deps: [Router],
          },
          {
            provide: APP_INITIALIZER,
            useFactory: () => () => {},
            deps: [Sentry.TraceService],
            multi: true,
          },
        ]
      : []),
    // Ngxs providers
    provideStore(
      [...stateList],
      withNgxsStoragePlugin({ keys: [...persistList] }),
      withNgxsReduxDevtoolsPlugin({
        disabled: environment.production,
        maxAge: 100,
      }),
      withNgxsResetPlugin(),
    ),
    //Quill editor
    provideQuillConfig({
      modules: {
        toolbar: [
          [
            {
              font: [
                'Arial, sans-serif',
                'Arial Black, sans-serif',
                'Book Antiqua, serif',
                'Century Gothic, sans-serif',
                'Comic Sans MS, sans-serif',
                'Courier New, monospace',
                'Garamond, serif',
                'Georgia, serif',
                'Helvetica, sans-serif',
                'Impact, sans-serif',
                'Lucida Console, monospace',
                'Lucida Sans Unicode, sans-serif',
                'Tahoma, sans-serif',
                'Times New Roman, serif',
                'Trebuchet MS, sans-serif',
                'Verdana, sans-serif',
              ],
            },
          ],
          ['bold', 'italic', 'underline'], // toggled buttons
          ['blockquote'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ script: 'sub' }, { script: 'super' }], // superscript/subscript
          [{ indent: '-1' }, { indent: '+1' }], // outdent/indent
          [{ direction: 'rtl' }], // text direction

          [
            {
              size: [
                '8px',
                '9px',
                '10px',
                '11px',
                '12px',
                '13px',
                '14px',
                '15px',
                '16px',
                '18px',
                '20px',
                '22px',
                '24px',
                '26px',
                '28px',
                '30px',
                '32px',
                '36px',
                '40px',
                '44px',
                '48px',
                '50px',
                '60px',
                '72px',
              ],
            },
          ], // custom dropdown
          [{ header: [1, 2, 3, 4, 5, 6, false] }],

          [{ color: [] }, { background: [] }], // dropdown with defaults from theme
          [{ align: [] }],

          ['link'], // link and image, video
          ['clean'],
        ],
      },
      customOptions: [
        {
          import: 'attributors/style/font',
          whitelist: [
            'Arial, sans-serif',
            'Arial Black, sans-serif',
            'Book Antiqua, serif',
            'Century Gothic, sans-serif',
            'Comic Sans MS, sans-serif',
            'Courier New, monospace',
            'Garamond, serif',
            'Georgia, serif',
            'Helvetica, sans-serif',
            'Impact, sans-serif',
            'Lucida Console, monospace',
            'Lucida Sans Unicode, sans-serif',
            'Tahoma, sans-serif',
            'Times New Roman, serif',
            'Trebuchet MS, sans-serif',
            'Verdana, sans-serif',
          ],
        },
        {
          import: 'attributors/style/size',
          whitelist: [
            '8px',
            '9px',
            '10px',
            '11px',
            '12px',
            '13px',
            '14px',
            '15px',
            '16px',
            '18px',
            '20px',
            '22px',
            '24px',
            '26px',
            '28px',
            '30px',
            '32px',
            '36px',
            '40px',
            '44px',
            '48px',
            '50px',
            '60px',
            '72px',
          ],
        },
      ],
    }),
  ],
};

export function preloadUserLanguage(transloco: TranslocoService) {
  return function () {
    const lang = localStorage.getItem(LANGUAGE_KEY) || 'en';
    transloco.setActiveLang(lang);
    return transloco.load(lang).toPromise();
  };
}
